import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { BasicService } from 'src/app/shared/services/basic.service';
import { KakaotalkTemplatesService } from '../kakaotalk-templates.service';
import { KakaotalkTemplate, KakaotalkTemplateCategoryEnum, KakaotalkTemplateUsageEnum } from '../shared';
import { KakaotalkTemplateForm } from '../shared/models/kakaotalk-template-form.model';

@Component({
  selector: 'app-kakaotalk-template',
  templateUrl: './kakaotalk-template.component.html',
  styleUrls: ['./kakaotalk-template.component.scss'],
})
export class KakaotalkTemplateComponent implements OnInit, OnDestroy {
  @Input() create: boolean;

  maxLength = this.kakaotalkTemplatesService.getLimitations().maxLength;
  form = this.fb.group({
    main: this.fb.group({
      name: [
        null,
        [Validators.required, Validators.pattern('^[a-z0-9_]+$'), Validators.maxLength(this.maxLength.templateName)],
      ],
      languages: [[], [Validators.required, Validators.minLength(1)]],
      siteId: [null, Validators.required],
      usages: [
        [KakaotalkTemplateUsageEnum.advisor, KakaotalkTemplateUsageEnum.bot],
        [Validators.required, Validators.minLength(1)],
      ],
    }),
    category: [KakaotalkTemplateCategoryEnum.FRIENDSTALK, Validators.required],
    templates: this.fb.array([]),
  });

  templateForm: FormGroup;
  template: KakaotalkTemplateForm;
  selectedIndex = 0;
  templateDisplayedBS: BehaviorSubject<FormGroup> = new BehaviorSubject(null);

  get mainForm() {
    return this.form?.get('main') as FormGroup;
  }

  get templatesForm() {
    return this.form?.get('templates') as FormArray;
  }

  get templatesLanguageActive() {
    return this.templatesForm.controls.filter((i: FormGroup) => i.getRawValue().active) as FormGroup[];
  }
  /**
   * The top toolbar save button configuration.
   */
  saveButtonConfig: any;

  languageDisplayed: KakaotalkTemplate;

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    public kakaotalkTemplatesService: KakaotalkTemplatesService,
    private dialog: MatDialog,
    private basicService: BasicService,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    // Mechanism to update the breadcrumb in the top toolbar.
    this.topToolbarService.setTitleTranslation('parameters/kakaotalk-templates/title', null);
    this.topToolbarService.setButton('save', () => this.askForSave());
  }

  ngOnInit() {
    if (!this.create) {
      this.basicService.showSpinner();

      this.kakaotalkTemplatesService
        .findOne(this.$stateParams.siteId, this.$stateParams.uuid)
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe({
          next: (template: KakaotalkTemplateForm) => {
            if (!template) {
              return;
            }
            this.kakaotalkTemplatesService.headerMediaFileBS.next(null);
            this.template = template;
            this.addFormListeners();
            this.form.get('main.languages').setValue(template.main.languages);
            this.form.patchValue(template);
            this.form.get('main').disable({ emitEvent: false });
            this.form.get('main.usages').enable();
          },
        });
    } else {
      this.addFormListeners();
    }
  }

  private addFormListeners() {
    this.topToolbarService.updateButtonDisable(true);
    this.subscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        this.templateDisplayedBS.next(this.templatesLanguageActive[this.selectedIndex]?.controls.template as FormGroup);

        const invalid =
          this.form.pristine ||
          this.form.get('main').invalid ||
          this.form.get('category').invalid ||
          (this.form.get('templates') as FormArray).controls.some(
            (templateControl: FormGroup) => templateControl.invalid && templateControl.get('active').value
          );
        this.topToolbarService.updateButtonDisable(invalid);
      })
    );

    this.subscriptions.add(
      this.form.get('main.languages').valueChanges.subscribe(languagesCodes => {
        if (languagesCodes?.length) {
          languagesCodes.forEach(languageCode => {
            const hasTemplate = this.templatesForm.getRawValue().some(template => template.code === languageCode);
            if (!hasTemplate) {
              this.templatesForm.push(
                this.fb.group({
                  code: [languageCode],
                  active: [true],
                  template: this.fb.group({
                    id: [null],
                    header: this.fb.group({
                      media_type: ['image'],
                      media_url: [null],
                      media_id: [null],
                      static: [0],
                    }),
                    body: [null, [Validators.required, Validators.maxLength(this.maxLength.body)]],
                    buttons: this.fb.array([]),
                    placeholders: this.fb.group({}),
                  }),
                })
              );
            } else {
              this.templatesForm.controls.forEach((control: FormGroup) => {
                const { code } = control.getRawValue();
                control.get('active').setValue(languagesCodes.includes(code));
              });
            }
          });
          if (!this.templateForm) {
            this.templateForm = this.templatesForm?.controls[this.selectedIndex] as FormGroup;
          }
        }
      })
    );

    this.templateForm = this.templatesForm?.controls[this.selectedIndex] as FormGroup;
  }

  selectedIndexChange({ index }) {
    this.selectedIndex = index;
    this.templateForm = this.templatesLanguageActive[index];
    this.templateDisplayedBS.next(this.templatesLanguageActive[index]?.controls.template as FormGroup);
  }

  /**
   * Allows to ask the user to confirm to save the template due to the constraints limitations from Kakaotalk.
   * @param template the template to save.
   */
  askForSave() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('parameters/kakaotalk-templates/save/title'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('continue'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.topToolbarService.updateButtonDisable(true);
        const model: KakaotalkTemplateForm = this.form.getRawValue();
        (model.templates || []).forEach((language: any) => {
          try {
            if (!language.template.header.media_id) {
              delete language.template.header;
            }
          } catch {
            // Do nothing;
          }
        });
        const languageGroupId = this.template?.metadata?.language_group_id;
        this.kakaotalkTemplatesService
          .save(model, languageGroupId)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe({
            next: (uuid: string) => {
              this.basicService.toastSuccess('OK');
              const { siteId } = model.main;
              this.kakaotalkTemplatesService.navigateToDetails(uuid, siteId);
            },
          });
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.topToolbarService.clearButtons();
  }
}
